import React from "react";
import { CYCLE_VALUE_BASES } from "./draftModel";
import { NumberField, Section, SelectField } from "./fields";

export const SEGMENT_MARGINS_NOTE = "Dealer margins come from the bike segments below.";

const PricingSection = ({ form, setForm, msgs, locked }) => {
  const rule = form.pricingRule;
  const set = (field, value) =>
    setForm((prev) => ({ ...prev, pricingRule: { ...prev.pricingRule, [field]: value } }));
  const setFactor = (months, value) =>
    setForm((prev) => ({
      ...prev,
      pricingRule: {
        ...prev.pricingRule,
        tenureFactors: { ...prev.pricingRule.tenureFactors, [months]: value },
      },
    }));
  const rupees = (field, label, help) => (
    <div className="col-md-4">
      <NumberField
        id={`warranty-rule-${field}`}
        label={label}
        kind="rupees"
        value={rule[field]}
        disabled={locked}
        help={help}
        messages={msgs(`pricingRule.${field}`)}
        onChange={(value) => set(field, value)}
      />
    </div>
  );

  return (
    <Section
      id="pricing"
      title="Pricing"
      description="The one-time price is a share of the cycle's MRP, held between the floor and the cap, then rounded. Monthly instalments add an uplift. Entered in rupees; the OMS stores whole paise."
    >
      <div className="row">
        <div className="col-md-6">
          <SelectField
            id="warranty-rule-cycleValueBasis"
            label="Price the plan on"
            value={rule.cycleValueBasis}
            options={CYCLE_VALUE_BASES}
            disabled={locked}
            help={
              rule.cycleValueBasis === "website_paid_price"
                ? "Website orders use the price the rider paid. Dealer, marketplace and old-invoice cycles use the MRP. Applies to the plan, the add-ons and an MRP-based coupon."
                : rule.cycleValueBasis === "website_price"
                ? "Every cycle, website, dealer, marketplace and old-invoice alike, uses the price the website shows for it today. A cycle the website shows no price for uses the MRP. Applies to the plan, the add-ons and an MRP-based coupon."
                : "The cycle's MRP in the OMS, for every rider."
            }
            messages={msgs("pricingRule.cycleValueBasis")}
            onChange={(value) => set("cycleValueBasis", value)}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          <NumberField
            id="warranty-rule-baseBps"
            label={rule.cycleValueBasis === "cycle_mrp" ? "Share of MRP" : "Share of the cycle price"}
            kind="percent"
            value={rule.baseBps}
            disabled={locked}
            messages={msgs("pricingRule.baseBps")}
            onChange={(value) => set("baseBps", value)}
          />
        </div>
        {rupees("floorMinor", "Floor", "The lowest one-time price.")}
        {rupees("capMinor", "Cap", "The highest one-time price.")}
        {rupees("roundingStepMinor", "Rounding step", "Prices are rounded to a multiple of this.")}
        {rupees("roundingEndingMinor", "Rounding ending", "Added after rounding, below the step.")}
        <div className="col-md-4">
          <NumberField
            id="warranty-rule-taxRateBps"
            label="Tax rate"
            kind="percent"
            value={rule.taxRateBps}
            disabled={locked}
            placeholder="Market default"
            help="Leave blank for the market's default tax rate."
            messages={msgs("pricingRule.taxRateBps")}
            onChange={(value) => set("taxRateBps", value)}
          />
        </div>
      </div>
      <h6 className="warranty-subhead">Monthly instalments</h6>
      <div className="row">
        <div className="col-md-4">
          <NumberField
            id="warranty-rule-monthlyUpliftBps"
            label="Monthly uplift"
            kind="percent"
            value={rule.monthlyUpliftBps}
            disabled={locked}
            help="Added to the one-time price before it is split."
            messages={msgs("pricingRule.monthlyUpliftBps")}
            onChange={(value) => set("monthlyUpliftBps", value)}
          />
        </div>
        {rupees("monthlyRoundingStepMinor", "Instalment rounding step")}
        {rupees("monthlyRoundingEndingMinor", "Instalment rounding ending")}
      </div>
      {form.tenures.filter((tenure) => tenure !== "").length > 0 && (
        <>
          <h6 className="warranty-subhead">Uplift for a tenure</h6>
          <p className="warranty-help">Leave blank to use the monthly uplift above.</p>
          <div className="row">
            {form.tenures
              .filter((tenure) => tenure !== "")
              .map((tenure) => (
                <div className="col-md-4" key={tenure}>
                  <NumberField
                    id={`warranty-factor-${tenure}`}
                    label={`${tenure} months`}
                    kind="percent"
                    value={rule.tenureFactors[tenure] === undefined ? "" : rule.tenureFactors[tenure]}
                    disabled={locked}
                    placeholder="Use monthly uplift"
                    messages={msgs(`pricingRule.tenureFactors.${tenure}`)}
                    onChange={(value) => setFactor(tenure, value)}
                  />
                </div>
              ))}
          </div>
        </>
      )}
      <h6 className="warranty-subhead">Dealer margin</h6>
      <p className="warranty-muted">
        What the dealer earns out of this plan on a bike the dealer sold. It comes out of the price, never on top;
        on monthly plans it is paid in the first payment. Website sales carry none.
      </p>
      {/* With bike segments on, each segment's margin is the only one used. These two stay in the
          form and are still sent as they were, so turning segments off brings them back. */}
      {rule.segmentsEnabled ? (
        <p className="warranty-help">{SEGMENT_MARGINS_NOTE}</p>
      ) : (
        <div className="row">
          {rupees("dealerMarginMinor", "Dealer margin (normal bikes)")}
          {rupees("dealerMarginOverrideMinor", "Dealer margin (override groups)", "When this plan has an override for the bike's product group.")}
        </div>
      )}
    </Section>
  );
};

export default PricingSection;
